import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("unknown"); return; }
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current < 12) {
        setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-4" data-testid="payment-success-page">
      <div className="bg-surface border border-line rounded-2xl p-10 max-w-md w-full text-center shadow-sm">
        <div className="flex justify-center mb-6"><Logo size={44} showText={false} /></div>
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-teal mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-serif font-semibold text-navy">Confirming your payment...</h1>
            <p className="mt-2 text-sm text-mutedink">This takes just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-sage mx-auto mb-4" />
            <h1 className="text-xl font-serif font-semibold text-navy" data-testid="payment-confirmed-title">Payment received. Thank you.</h1>
            <p className="mt-3 text-sm text-mutedink leading-relaxed">Your payment has been applied and your receipt is in your portal payment history.</p>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="h-10 w-10 text-champagne-dark mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-serif font-semibold text-navy">Payment processing</h1>
            <p className="mt-3 text-sm text-mutedink leading-relaxed">Your payment is still being confirmed by the card network. Check your portal payment history in a few minutes.</p>
          </>
        )}
        {status === "unknown" && (
          <>
            <h1 className="text-xl font-serif font-semibold text-navy">Missing payment reference</h1>
            <p className="mt-3 text-sm text-mutedink">We could not identify this payment session.</p>
          </>
        )}
        <Button asChild className="mt-8 bg-teal text-white hover:bg-teal-dark rounded-full w-full" data-testid="back-to-payments-button">
          <Link to="/portal/payments">Back to my payments</Link>
        </Button>
      </div>
    </div>
  );
}
